import { parseFrontmatter } from "../utils/parse-frontmatter";
import type { Article, CategoryGroup } from "../types/article";

// Eagerly load every markdown article as a raw string at build time. Adding an
// article is just dropping a new .md file in this folder — no code change here.
const files = import.meta.glob<string>("./*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

function slugFromPath(path: string): string {
	return path.split("/").pop()!.replace(/\.md$/, "");
}

function buildArticles(): Array<Article> {
	const articles = Object.entries(files).map(([path, raw]) => {
		const { meta, body } = parseFrontmatter(raw, path);
		return { slug: slugFromPath(path), body, ...meta };
	});

	if (articles.length === 0) {
		throw new Error("Kennisbank has no articles — add a .md file to content/.");
	}

	return articles.sort((a, b) => a.order - b.order);
}

export const ARTICLES = buildArticles();

const BY_SLUG = new Map(ARTICLES.map((article) => [article.slug, article]));

export function getArticle(slug: string): Article | undefined {
	return BY_SLUG.get(slug);
}

// Group by category, preserving the order in which each category first appears
// in the order-sorted list (so a category's position follows its earliest article).
export const CATEGORIES: Array<CategoryGroup> = (() => {
	const groups = new Map<string, Array<Article>>();
	for (const article of ARTICLES) {
		const existing = groups.get(article.category);
		if (existing) {
			existing.push(article);
		} else {
			groups.set(article.category, [article]);
		}
	}
	return [...groups.entries()].map(([category, articles]) => ({
		category,
		articles,
	}));
})();
